import { useState } from 'react';
import dayjs from 'dayjs';
import { DateTime as DT } from 'luxon';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import AppDatePicker from 'components/dateTime/AppDatePicker';
import AppTimePicker from 'components/dateTime/AppTimePicker';
import { inclusiveTermDays, isoDateFromTermDays } from './helpers/taskDueDateHelpers';

export interface TaskDueDateFieldsProps {
    multiDay: boolean;
    withTime: boolean;
    dateStart: number;
    dateEnd: number;
    startTime: string;
    endTime: string;
    onMultiDayChange: (checked: boolean) => void;
    onWithTimeChange: (checked: boolean) => void;
    onStartDateChange: (isoDate: string) => void;
    onEndDateChange: (isoDate: string) => void;
    onStartTimeChange: (time: string) => void;
    onEndTimeChange: (time: string) => void;
    idPrefix?: string;
    hideLabel?: boolean;
}

const millisToIsoDate = (millis: number | null | undefined): string => {
    const value = Number(millis || 0);
    if (!Number.isFinite(value) || value <= 0) return '';

    return DT.fromMillis(value, { zone: 'local' }).toISODate() || '';
};

const TaskDueDateFields = ({
    multiDay,
    withTime,
    dateStart,
    dateEnd,
    startTime,
    endTime,
    onMultiDayChange,
    onWithTimeChange,
    onStartDateChange,
    onEndDateChange,
    onStartTimeChange,
    onEndTimeChange,
    idPrefix = 'task',
    hideLabel = false,
}: TaskDueDateFieldsProps) => {
    const { t } = useTranslation(['Tasks']);
    const [daysFocused, setDaysFocused] = useState(false);
    const [daysDraft, setDaysDraft] = useState('');

    const multiDayId = `${idPrefix}-multi-day`;
    const withTimeId = `${idPrefix}-with-time`;

    const today = dayjs().startOf('day');
    const startIso = millisToIsoDate(dateStart);
    const endIso = millisToIsoDate(dateEnd);
    const endMinDate = multiDay && startIso
        ? dayjs(startIso).isAfter(today, 'day') ? dayjs(startIso) : today
        : today;

    const showStart = multiDay || withTime;
    const showDuration = multiDay && !withTime;
    const derivedDays = inclusiveTermDays(dateStart, dateEnd);
    const daysValue = daysFocused ? daysDraft : String(derivedDays);

    const handleDaysChange = (raw: string) => {
        if (raw !== '' && !/^\d+$/.test(raw)) return;
        setDaysDraft(raw);
        const days = Number(raw);
        if (!Number.isInteger(days) || days < 1) return;
        const iso = isoDateFromTermDays(dateStart, days);
        if (iso) onEndDateChange(iso);
    };

    return (
        <div>
            <div className="mb-2">
                {!hideLabel && (
                    <label className="form-label text-muted mb-2">
                        {t('labelDueDate')}
                    </label>
                )}

                <div className="d-flex flex-nowrap align-items-center gap-3 mb-3">
                    <div className="form-check form-switch mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id={multiDayId}
                            checked={multiDay}
                            onChange={(e) => onMultiDayChange(e.target.checked)}
                        />
                        <label
                            htmlFor={multiDayId}
                            className="form-check-label text-nowrap"
                        >
                            {t('labelMultiDay')}
                        </label>
                    </div>

                    <div className="form-check form-switch mb-0">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            role="switch"
                            id={withTimeId}
                            checked={withTime}
                            onChange={(e) => onWithTimeChange(e.target.checked)}
                        />
                        <label
                            htmlFor={withTimeId}
                            className="form-check-label text-nowrap"
                        >
                            {t('labelWithTime')}
                        </label>
                    </div>
                </div>
            </div>

            <Row>
                {showStart && (
                    <Col sm={showDuration ? 5 : multiDay ? 6 : 4}>
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('labelDateStart')}
                            </label>

                            <div className="d-flex gap-2">
                                {multiDay && (
                                    <AppDatePicker
                                        value={startIso}
                                        disabledDate={(date) => date.isBefore(today, 'day')}
                                        onChange={onStartDateChange}
                                        style={{ minWidth: 0, flex: '1 1 0' }}
                                    />
                                )}

                                {withTime && (
                                    <AppTimePicker
                                        value={startTime || '00:00'}
                                        onChange={onStartTimeChange}
                                        style={{ minWidth: 0, flex: '1 1 0' }}
                                    />
                                )}
                            </div>
                        </Form.Group>
                    </Col>
                )}

                {showDuration && (
                    <Col sm={2}>
                        <Form.Group className="mb-3">
                            <label className="form-label text-muted">
                                {t('labelTermDaysShort')}
                            </label>
                            <Form.Control
                                type="number"
                                min={1}
                                step={1}
                                inputMode="numeric"
                                value={daysValue}
                                onFocus={() => {
                                    setDaysDraft(String(derivedDays));
                                    setDaysFocused(true);
                                }}
                                onBlur={() => setDaysFocused(false)}
                                onChange={(e) => handleDaysChange(e.target.value.trim())}
                            />
                        </Form.Group>
                    </Col>
                )}

                <Col sm={showDuration ? 5 : multiDay ? 6 : withTime ? 8 : 12}>
                    <Form.Group className="mb-3">
                        <label className="form-label text-muted">
                            {t('labelDateEnd')}
                        </label>

                        <div className="d-flex gap-2">
                            <AppDatePicker
                                value={endIso}
                                disabledDate={(date) => date.isBefore(endMinDate, 'day')}
                                onChange={onEndDateChange}
                                style={{ minWidth: 0, flex: '1 1 0' }}
                            />

                            {withTime && (
                                <AppTimePicker
                                    value={endTime || '18:00'}
                                    onChange={onEndTimeChange}
                                    style={{ minWidth: 0, flex: '1 1 0' }}
                                />
                            )}
                        </div>
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
};

export default TaskDueDateFields;
